import { AppError, isAppError } from "@shared/app-error";
import type { AnalyzeFrameResult } from "@shared/worker-protocol";
import type { WorkerClient } from "./worker-client";

/**
 * <video> から VideoFrame を取り出して Worker へ送り、 結果を購読する rAF ループ。
 *
 * §4.1 / §3.3: 処理 fps を `capFps` (デフォルト 15fps) に cap する。 Worker は
 * in-flight 1 件直列化されているため前回の RPC 完了まで次フレームをスキップするが、
 * それだけだと高リフレッシュ端末で display refresh (~60Hz) 近くまで回り、 検知側の
 * 窓 (issue #230) と組み合わさって品質が崩れる。 throttle で処理密度をコーパス
 * (~16fps) regime に固定する (issue #231、 cap 値の floor は issue #232 で検証)。
 * `processFrame` の RTT が 1000/capFps を越えると実効 fps はさらに落ちるが、
 * 数値誤検知の精度には影響しない。
 *
 * §3.3: PiP Window 確保前は main の rAF、 確保後は PiP Window の rAF で動かす。
 * 呼び出し側で適切な window の `requestAnimationFrame` を渡すこと。
 *
 * §4.7.4: エラーは `onError` に通知し、 ループは継続 (transient policy 相当)。
 * fatal な AppError なら onError 経由で呼び出し側がループ停止を判断する。
 *
 * issue #130: audio VAD 撤去で audio payload (Float32Array) は process-frame RPC から消えた。
 * worker は VideoFrame のみを受け取り、 raw signal (mar / blendshapes / lipLandmarks / headPose) を返す。
 */

/**
 * デフォルトは window.requestAnimationFrame。 PiP Window のループは
 * `pipWindow.requestAnimationFrame` を渡すこと (§5.2)。
 */
export type RafBindings = {
  rafProvider?: (cb: FrameRequestCallback) => number;
  cancelRaf?: (handle: number) => void;
};

/**
 * §3.3: PiP Window がある間はその rAF を使い、 無ければ main window の rAF にフォールバックする。
 * ミュート解除や PiP 手動クローズで rAF source を切り替えるときの共通ヘルパー。
 */
export function pipRafBindings(pipWindow: Window | null): RafBindings {
  return pipWindow
    ? {
        rafProvider: pipWindow.requestAnimationFrame.bind(pipWindow),
        cancelRaf: pipWindow.cancelAnimationFrame.bind(pipWindow),
      }
    : {};
}

type FrameLoopOptions = RafBindings & {
  video: HTMLVideoElement;
  worker: WorkerClient;
  onResult: (r: AnalyzeFrameResult) => void;
  onError?: (err: unknown) => void;
  /** デバッグ表示用の解析完了レート (EMA 平滑化済み)。 vsync レートではなく onResult 到着間隔から算出。 */
  onFps?: (fps: number) => void;
  /** 処理 fps の上限。 未指定時は PROCESSING_CAP_FPS。 テストで小さい値を注入して throttle を決定的に検証する。 */
  capFps?: number;
};

const FPS_EMA_ALPHA = 0.2;
// 本番処理 fps の cap 本命値 (issue #231)。 高リフレッシュ端末で検知窓が崩れるのを防ぐため
// 処理密度をコーパス (~16fps) regime に固定する。 floor の最終確定は issue #232 (fps 下方向スイープ)。
const PROCESSING_CAP_FPS = 15;
// §UX: PiP 切替経路の stop()→restart の drain ループ上限。 Worker freeze / OS thread suspension で
// in-flight が永久に解消しないケースで reopenPipWindow / handlePipClosed が hang するのを防ぐ。
// teardown 経路は workerClient.terminate() で in-flight が即 reject されるためここには到達しない。
const STOP_DRAIN_DEADLINE_MS = 2_000;
const STOP_DRAIN_INTERVAL_MS = 16;

export function startFrameLoop(options: FrameLoopOptions): () => Promise<void> {
  const raf = options.rafProvider ?? requestAnimationFrame.bind(window);
  const cancel = options.cancelRaf ?? cancelAnimationFrame.bind(window);
  const minFrameIntervalMs = 1000 / (options.capFps ?? PROCESSING_CAP_FPS);

  let running = true;
  let inFlight = false;
  let pendingHandle = 0;
  // throttle 用: 実際に処理に入ったフレームの送信時刻。 skip した tick では更新しない。
  let lastProcessedAt = 0;
  // FPS は「解析完了レート」を測りたい (vsync 由来の rAF 着信レートではなく Worker RPC を含めた完了周期)。
  let lastResultAt = 0;
  let smoothedFps = 0;

  const tick = async (): Promise<void> => {
    if (!running) return;
    // §UX: rAF source (main window / PiP Window) を切り替えても MediaPipe の単調増加 timestamp 制約
    // (Packet timestamp mismatch) を維持するため、 raf callback の timestamp 引数は使わず
    // main thread の performance.now() に統一する。 throttle 判定と frame timestamp に再利用する。
    const now = performance.now();
    // cap throttle: 前回処理から minFrameIntervalMs 未満なら当該 tick を skip (issue #231)。
    if (
      inFlight ||
      options.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
      now - lastProcessedAt < minFrameIntervalMs
    ) {
      pendingHandle = raf(tick);
      return;
    }
    inFlight = true;
    lastProcessedAt = now;
    try {
      const timestamp = now;
      const videoFrame = new VideoFrame(options.video, { timestamp: Math.floor(timestamp * 1000) });
      const result = await options.worker.call(
        { type: "process-frame", payload: { videoFrame, timestamp } },
        [videoFrame],
      );
      // await 中に stopFrameLoop が呼ばれている可能性。 シャットダウン後は結果を上位に流さない。
      if (!running) return;
      options.onResult(result);
      // fps は frame 送信時刻の間隔から算出。 Worker は in-flight 1 件直列化なので
      // 「送信間隔 ≒ 結果到着間隔」になり、 結果到着時刻を別途取らなくても傾向は掴める。
      // (厳密には Worker RTT 分だけ dt が短くなり表示 FPS が高めにバイアスするが、
      //  デバッグ表示のみで機能影響なしと判断して許容。)
      if (lastResultAt > 0) {
        const dt = timestamp - lastResultAt;
        if (dt > 0) {
          const instant = 1000 / dt;
          smoothedFps = smoothedFps === 0 ? instant : smoothedFps + FPS_EMA_ALPHA * (instant - smoothedFps);
          options.onFps?.(smoothedFps);
        }
      }
      lastResultAt = timestamp;
    } catch (err) {
      // teardown 中 (= stopFrameLoop が呼ばれた後 / workerClient.terminate() による in-flight reject)
      // は意図的シャットダウンなので transient エラーは握り潰す。 ただし真の fatal AppError
      // (MediaPipeInitFailed 等) は teardown 中であっても上位に届けないと運用上の隠蔽事故になる。
      if (!running && !isFatalError(err)) return;
      // transient な AppError は次フレームで自動的にリトライされる。 fatal な
      // AppError は呼び出し側で onError を見てループ停止する責務 (5.8.2 の leaf)。
      options.onError?.(toAppError(err));
    } finally {
      inFlight = false;
      if (running) pendingHandle = raf(tick);
    }
  };

  pendingHandle = raf(tick);

  return async () => {
    running = false;
    cancel(pendingHandle);
    // §UX: PiP 手動クローズ等で frame-loop を再起動するとき、 同じ workerClient に対して
    // 新しい tick が次の RPC を送ると in-flight 1 件直列化に違反する (WorkerRpcInFlight)。
    // teardown 経由 (terminate で in-flight reject) では即解放されるため、 ここでは
    // 同じ worker の再利用 = 再起動シナリオでの整合性のために in-flight 解消を待つ。
    // Worker freeze 等で永久 hang しないよう deadline 経過で諦める。 諦めた場合は
    // 呼び出し側 (reopenPipWindow / handlePipClosed) が新しい frame-loop を起動して
    // RPC を発射することになるが、 そこで WorkerRpcInFlight になれば leaf の onError → fatal で
    // セッション停止 + Sentry 通知され、 沈黙よりは観測可能になる。
    const deadline = Date.now() + STOP_DRAIN_DEADLINE_MS;
    while (inFlight && Date.now() < deadline) {
      await new Promise<void>((resolve) => setTimeout(resolve, STOP_DRAIN_INTERVAL_MS));
    }
  };
}

function toAppError(err: unknown): AppError {
  if (isAppError(err)) return err;
  if (err instanceof Error) return new AppError("FrameDropped", "transient", err.message, err);
  return new AppError("FrameDropped", "transient", String(err));
}

function isFatalError(err: unknown): boolean {
  return isAppError(err) && err.policy === "fatal";
}
